import Plugin from '@ckeditor/ckeditor5-core/src/plugin'
import DowncastDispatcher, { DowncastConversionApi } from '@ckeditor/ckeditor5-engine/src/conversion/downcastdispatcher'
import { AnchorCommand } from './AnchorCommand'
import { UnanchorCommand } from './UnanchorCommand'
import TwoStepCaretMovement from '@ckeditor/ckeditor5-typing/src/twostepcaretmovement'
import inlineHighlight from '@ckeditor/ckeditor5-typing/src/utils/inlinehighlight'
import AttributeElement from '@ckeditor/ckeditor5-engine/src/view/attributeelement'
import MouseObserver from '@ckeditor/ckeditor5-engine/src/view/observer/mouseobserver'
import findAttributeRange from '@ckeditor/ckeditor5-typing/src/utils/findattributerange'
import { Model } from '@ckeditor/ckeditor5-engine'
import { keyCodes } from '@ckeditor/ckeditor5-utils/src/keyboard'
import Clipboard from '@ckeditor/ckeditor5-clipboard/src/clipboard'
import ImageInlineEditing from '@ckeditor/ckeditor5-image/src/image/imageinlineediting'
import ImageBlockEditing from '@ckeditor/ckeditor5-image/src/image/imageblockediting'

const HIGHLIGHT_CLASS = 'ck-anchor_selected'

export class AnchorEditing extends Plugin {
  static get pluginName(): string {
    return 'AnchorEditing'
  }

  static get requires(): [typeof TwoStepCaretMovement, typeof Clipboard, typeof AnchorImageEditing] {
    // Clipboard is required for handling cut and paste events while typing over the anchor.
    return [TwoStepCaretMovement, Clipboard, AnchorImageEditing]
  }

  init(): void {
    const editor = this.editor

    // Allow anchor attribute on all inline nodes.
    editor.model.schema.extend('$text', { allowAttributes: 'anchorId' })

    editor.conversion
      .for('dataDowncast')
      .attributeToElement({ model: 'anchorId', view: anchorElementCreator({ editing: false }) })

    editor.conversion
      .for('editingDowncast')
      .attributeToElement({ model: 'anchorId', view: anchorElementCreator({ editing: true }) })

    editor.conversion.for('upcast').elementToAttribute({
      view: {
        name: 'span',
        attributes: {
          id: true,
        },
      },
      model: {
        key: 'anchorId',
        value: (viewElement: HTMLElement) => viewElement.getAttribute('id') ?? null,
      },
    })

    // Create anchoring commands.
    editor.commands.add('anchor', new AnchorCommand(editor))
    editor.commands.add('unanchor', new UnanchorCommand(editor))

    // Enable two-step caret movement for `anchorId` attribute.
    const twoStepCaretMovementPlugin = editor.plugins.get(TwoStepCaretMovement)
    twoStepCaretMovementPlugin.registerAttribute('anchorId')

    // Setup highlight over selected anchor.
    inlineHighlight(editor, 'anchorId', 'span', HIGHLIGHT_CLASS)

    // Change the attributes of the selection in certain situations after the anchor was inserted into the document.
    this._enableInsertContentSelectionAttributesFixer()

    // Handle a click at the beginning/end of a anchor element.
    this._enableClickingAfterAnchor()

    // Handle typing over the anchor.
    this._enableTypingOverAnchor()

    // Handle removing the content after the anchor element.
    this._handleDeleteContentAfterAnchor()
  }

  /**
   * Starts listening to {@link module:engine/model/model~Model#event:insertContent} and corrects the model
   * selection attributes if the selection is at the end of a anchor after inserting the content.
   *
   * The purpose of this action is to improve the overall UX because the user is no longer "trapped" by the
   * `anchorId` attribute of the selection and they can type a "clean" (`anchorId`–less) text right away.
   *
   * See https://github.com/ckeditor/ckeditor5/issues/6053.
   */
  private _enableInsertContentSelectionAttributesFixer(): void {
    const editor = this.editor
    const model = editor.model
    const selection = model.document.selection

    this.listenTo(
      model,
      'insertContent',
      () => {
        const nodeBefore = selection.anchor?.nodeBefore
        const nodeAfter = selection.anchor?.nodeAfter

        // NOTE: ↰ and ↱ represent the gravity of the selection.

        // The only truly valid case is:
        //
        //		                                 ↰
        //		...<$text anchorId="foo">INSERTED[]</$text>
        //
        // If the selection is not "trapped" by the `anchorId` attribute after inserting, there's nothing
        // to fix there.
        if (!selection.hasAttribute('anchorId')) {
          return
        }

        // Filter out the following case where a anchor with the same id (e.g. <a id="foo">INSERTED</a>) is inserted
        // in the middle of an existing anchor:
        //
        // Before insertion:
        //		                       ↰
        //		<$text anchorId="foo">l[]ink</$text>
        //
        // Expected after insertion:
        //		                               ↰
        //		<$text anchorId="foo">lINSERTED[]ink</$text>
        //
        if (!nodeBefore) {
          return
        }

        // Filter out the following case where the selection has the "anchorId" attribute because the
        // gravity is overridden and some text with another attribute (e.g. <b>INSERTED</b>) is inserted:
        //
        // Before insertion:
        //
        //		                       ↱
        //		<$text anchorId="foo">[]anchor</$text>
        //
        // Expected after insertion:
        //
        //		                                                          ↱
        //		<$text bold="true">INSERTED</$text><$text anchorId="foo">[]anchor</$text>
        //
        if (!nodeBefore.hasAttribute('anchorId')) {
          return
        }

        // Filter out the following case where a anchor is a inserted in the middle (or before) another anchor
        // (different URLs, so they will not merge). In this (let's say weird) case, we can leave the selection
        // attributes as they are because the user will end up writing in one anchor or another anyway.
        //
        // Before insertion:
        //
        //		                       ↰
        //		<$text anchorId="foo">l[]ink</$text>
        //
        // Expected after insertion:
        //
        //		                                                             ↰
        //		<$text anchorId="foo">l</$text><$text anchorId="bar">INSERTED[]</$text><$text anchorId="foo">ink</$text>
        //
        if (nodeAfter && nodeAfter.hasAttribute('anchorId')) {
          return
        }

        model.change((writer) => {
          writer.removeSelectionAttribute('anchorId')
        })
      },
      { priority: 'low' },
    )
  }

  /**
   * Starts listening to {@link module:engine/view/document~Document#event:mousedown} and
   * {@link module:engine/view/document~Document#event:selectionChange} and puts the selection before/after a anchor node
   * if clicked at the beginning/ending of the anchor.
   *
   * The purpose of this action is to allow typing around the anchor node directly after a click.
   *
   * See https://github.com/ckeditor/ckeditor5/issues/1016.
   */
  private _enableClickingAfterAnchor(): void {
    const editor = this.editor

    editor.editing.view.addObserver(MouseObserver)

    let clicked = false

    // Detect the click.
    this.listenTo(editor.editing.view.document, 'mousedown', () => {
      clicked = true
    })

    // When the selection has changed...
    this.listenTo(editor.editing.view.document, 'selectionChange', () => {
      if (!clicked) {
        return
      }

      // ...and it was caused by the click...
      clicked = false

      const selection = editor.model.document.selection

      // ...and no text is selected...
      if (!selection.isCollapsed) {
        return
      }

      // ...and clicked text is the anchor...
      if (!selection.hasAttribute('anchorId')) {
        return
      }

      const position = selection.getFirstPosition()
      const anchorId = selection.getAttribute('anchorId')
      if (position && typeof anchorId === 'string') {
        const anchorRange = findAttributeRange(position, 'anchorId', anchorId, editor.model)

        // ...check whether clicked start/end boundary of the anchor.
        // If so, remove the `anchorId` attribute.
        if (position.isTouching(anchorRange.start) || position.isTouching(anchorRange.end)) {
          editor.model.change((writer) => {
            writer.removeSelectionAttribute('anchorId')
          })
        }
      }
    })
  }

  /**
   * Starts listening to {@link module:engine/model/model~Model#deleteContent} and {@link module:engine/model/model~Model#insertContent}
   * and checks whether typing over the anchor. If so, attributes of removed text are preserved and applied to the inserted text.
   *
   * The purpose of this action is to allow modifying a text without loosing the `anchorId` attribute (and other).
   *
   * See https://github.com/ckeditor/ckeditor5/issues/4762.
   */
  private _enableTypingOverAnchor(): void {
    const editor = this.editor
    const view = editor.editing.view

    // Selection attributes when started typing over the anchor.
    let selectionAttributes: IterableIterator<[string, unknown]> | null = null

    // Whether pressed `Backspace` or `Delete`. If so, attributes should not be preserved.
    let deletedContent = false

    // Detect pressing `Backspace` / `Delete`.
    this.listenTo(
      view.document,
      'delete',
      () => {
        deletedContent = true
      },
      { priority: 'high' },
    )

    // Listening to `model#deleteContent` allows detecting whether selected content was a anchor.
    // If so, before removing the element, we will copy its attributes.
    this.listenTo(
      editor.model,
      'deleteContent',
      () => {
        const selection = editor.model.document.selection

        // Copy attributes only if anything is selected.
        if (selection.isCollapsed) {
          return
        }

        // When the content was deleted, do not preserve attributes.
        if (deletedContent) {
          deletedContent = false

          return
        }

        if (shouldCopyAttributes(editor.model)) {
          selectionAttributes = selection.getAttributes()
        }
      },
      { priority: 'high' },
    )

    // Listening to `model#insertContent` allows detecting the content insertion.
    // We want to apply attributes that were removed while typing over the anchor.
    this.listenTo(
      editor.model,
      'insertContent',
      (evt, [element]) => {
        deletedContent = false

        if (!selectionAttributes) {
          return
        }

        const notNullSelectionAttributes = selectionAttributes

        editor.model.change((writer) => {
          for (const [attribute, value] of notNullSelectionAttributes) {
            writer.setAttribute(attribute, value, element)
          }
        })

        selectionAttributes = null
      },
      { priority: 'high' },
    )
  }

  /**
   * Starts listening to {@link module:engine/model/model~Model#deleteContent} and checks whether
   * removing a content right after the "anchorId" attribute.
   *
   * If so, the selection should not preserve the `anchorId` attribute. However, if
   * the {@link module:typing/twostepcaretmovement~TwoStepCaretMovement} plugin is active and
   * the selection has the "anchorId" attribute due to overriden gravity (at the end), the `anchorId` attribute should stay untouched.
   *
   * The purpose of this action is to allow removing the anchor text and keep the selection outside the anchor.
   *
   * See https://github.com/ckeditor/ckeditor5/issues/7521.
   */
  private _handleDeleteContentAfterAnchor(): void {
    const editor = this.editor
    const model = editor.model
    const selection = model.document.selection
    const view = editor.editing.view

    // A flag whether attributes `anchorId` attribute should be preserved.
    let shouldPreserveAttributes = false

    // A flag whether the `Backspace` key was pressed.
    let hasBackspacePressed = false

    // Detect pressing `Backspace`.
    this.listenTo(
      view.document,
      'delete',
      (evt, data) => {
        hasBackspacePressed = data.domEvent.keyCode === keyCodes.backspace
      },
      { priority: 'high' },
    )

    // Before removing the content, check whether the selection is inside a anchor or at the end of anchor but with 2-SCM enabled.
    // If so, we want to preserve anchor attributes.
    this.listenTo(
      model,
      'deleteContent',
      () => {
        // Reset the state.
        shouldPreserveAttributes = false

        const position = selection.getFirstPosition()
        const anchorId = selection.getAttribute('anchorId')

        if (typeof anchorId !== 'string' || !position) {
          return
        }

        const anchorRange = findAttributeRange(position, 'anchorId', anchorId, model)

        // Preserve `anchorId` attribute if the selection is in the middle of the anchor or
        // the selection is at the end of the anchor and 2-SCM is activated.
        shouldPreserveAttributes = anchorRange.containsPosition(position) || anchorRange.end.isEqual(position)
      },
      { priority: 'high' },
    )

    // After removing the content, check whether the current selection should preserve the `anchorId` attribute.
    this.listenTo(
      model,
      'deleteContent',
      () => {
        // If didn't press `Backspace`.
        if (!hasBackspacePressed) {
          return
        }

        hasBackspacePressed = false

        // Disable the mechanism if inside a anchor (`<$text url="foo">F[]oo</$text>` or <$text url="foo">Foo[]</$text>`).
        if (shouldPreserveAttributes) {
          return
        }

        // Use `model.enqueueChange()` in order to execute the callback at the end of the changes process.
        editor.model.enqueueChange((writer) => {
          writer.removeSelectionAttribute('anchorId')
        })
      },
      { priority: 'low' },
    )
  }
}

function anchorElementCreator(config: { editing: boolean }) {
  return (anchorId: string, { writer }: DowncastConversionApi): AttributeElement => {
    const anchorElement = writer.createAttributeElement('span', { id: anchorId, name: anchorId }, { priority: 5 })
    if (config.editing) {
      writer.addClass('ck-anchor', anchorElement)
    }
    writer.setCustomProperty('anchor', true, anchorElement)

    return anchorElement
  }
}

// Checks whether selection's attributes should be copied to the new inserted text.
function shouldCopyAttributes(model: Model): boolean {
  const selection = model.document.selection
  const firstPosition = selection.getFirstPosition()
  const lastPosition = selection.getLastPosition()
  const nodeAtFirstPosition = firstPosition?.nodeAfter
  const anchorId = nodeAtFirstPosition?.getAttribute('anchorId')

  // The text anchor node does not exist...
  if (!nodeAtFirstPosition) {
    return false
  }

  // ...or it isn't the text node...
  if (!nodeAtFirstPosition.is('$text')) {
    return false
  }

  // ...or isn't the anchor.
  if (typeof anchorId !== 'string') {
    return false
  }

  // `textNode` = the position is inside the anchor element.
  // `nodeBefore` = the position is at the end of the anchor element.
  const nodeAtLastPosition = lastPosition?.textNode || lastPosition?.nodeBefore

  // If both references the same node selection contains a single text node.
  if (nodeAtFirstPosition === nodeAtLastPosition) {
    return true
  }

  // If nodes are not equal, maybe the anchor nodes has defined additional attributes inside.
  // First, we need to find the entire anchor range.
  const anchorRange = findAttributeRange(firstPosition, 'anchorId', anchorId, model)

  // Then we can check whether selected range is inside the found anchor range. If so, attributes should be preserved.
  return anchorRange.containsRange(model.createRange(firstPosition, lastPosition ?? undefined), true)
}

export class AnchorImageEditing extends Plugin {
  static get pluginName(): string {
    return 'AnchorImageEditing'
  }

  static get requires(): [typeof ImageInlineEditing, typeof ImageBlockEditing] {
    return [ImageInlineEditing, ImageBlockEditing]
  }

  init(): void {
    const editor = this.editor

    editor.model.schema.extend('imageInline', { allowAttributes: 'anchorId' })
    editor.model.schema.extend('imageBlock', { allowAttributes: 'anchorId' })

    editor.conversion.for('dataDowncast').add(downcastImageAnchor({ editing: false, element: 'imageInline' }))
    editor.conversion.for('editingDowncast').add(downcastImageAnchor({ editing: true, element: 'imageInline' }))
    editor.conversion.for('dataDowncast').add(downcastImageAnchor({ editing: false, element: 'imageBlock' }))
    editor.conversion.for('editingDowncast').add(downcastImageAnchor({ editing: true, element: 'imageBlock' }))

    editor.conversion.for('upcast').attributeToAttribute({
      view: 'id',
      model: 'anchorId',
    })
  }
}

function downcastImageAnchor(config: { editing: boolean; element: string }) {
  return (dispatcher: DowncastDispatcher) => {
    dispatcher.on(`attribute:anchorId:${config.element}`, (event, data, { writer, mapper, consumable }) => {
      if (!consumable.test(data.item, event.name)) {
        return
      }

      consumable.consume(data.item, event.name)

      const imageInline = mapper.toViewElement(data.item)

      if (!imageInline) {
        throw Error('')
      }

      if (data.attributeNewValue === null) {
        writer.removeAttribute('id', imageInline)
        if (config.editing) {
          writer.removeClass('ck-anchor', imageInline)
        }
      } else {
        writer.setAttribute('id', data.attributeNewValue, imageInline)
        if (config.editing) {
          writer.addClass('ck-anchor', imageInline)
        }
        writer.setCustomProperty('anchor', true, imageInline)
      }
    })
  }
}
